/**
 * Pure helpers for the Advisory (SSO) login: building the hand-off URL and
 * vetting URLs that come from env.
 *
 * Separate from HomePage so the URL logic is testable without rendering, and so
 * the one rule that matters — only ever navigate to http(s) — has one home.
 */

/**
 * Parse a URL that came from configuration, accepting http(s) only.
 *
 * `new URL()` alone is not a safe gate: `javascript:alert(1)` parses fine, and
 * both of these end up as a link or a navigation target. Legacy's sanitizeUrl
 * (Home.vue:177) has exactly that gap. An env value is developer-controlled, so
 * this is hardening rather than a live hole — but a navigation target is the
 * wrong place to rely on that.
 *
 * @param {string | undefined | null} value
 * @returns {URL | null}
 */
function parseHttpUrl(value) {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url : null;
  } catch {
    return null;
  }
}

/**
 * Where "Sign in with AMD" sends the browser.
 *
 * Legacy (Home.vue:163-170) does `${redirectUri}?appName=${appName}` — a full
 * page redirect to the Common Services app, which runs the SSO, writes the
 * shared *.amd.com cookies and sends the user back. `appName` tells it which
 * product to return to.
 *
 * Built with URL rather than string concatenation so a base that already
 * carries a query string gets `&appName=` instead of a second `?`. The visible
 * difference from legacy is a normalised trailing slash on a bare host
 * (`https://host/?appName=CCA`), which is the same address.
 *
 * @param {string} csHomeUrl  the Common Services app (env.csHomeUrl)
 * @param {string} appName    'CCA' | 'EIA'
 * @returns {string | null}   null when csHomeUrl is unset or not http(s)
 */
export function buildSsoUrl(csHomeUrl, appName) {
  const url = parseHttpUrl(csHomeUrl);
  if (!url) return null;
  url.searchParams.set('appName', appName);
  return url.toString();
}

/**
 * A configured link target, normalised — or null if it should not be rendered.
 *
 * @param {string | undefined | null} value
 * @returns {string | null}
 */
export function safeHttpUrl(value) {
  return parseHttpUrl(value)?.toString() ?? null;
}
